import { normalizePhone } from "@commerce-os/utils";
import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { generatedFileResponseSchema } from "./imports.js";
import { paisa } from "./money.js";
/**
 * The platform's own billing (docs/specs/billing.md, SA-08 … SA-13): subscriptions, platform invoices, and — for now —
 * manual payments (bKash Send Money or cash) approved by the platform admin.
 */
export const BILLING_CYCLES = ["monthly", "yearly"];
export const billingCycleSchema = z.enum(BILLING_CYCLES);
export const PAYMENT_REQUEST_METHODS = ["bkash_manual", "cash"];
export const paymentRequestMethodSchema = z.enum(PAYMENT_REQUEST_METHODS);
export const PAYMENT_REQUEST_STATUSES = ["submitted", "approved", "rejected", "withdrawn"];
/** `addon`: extra locations / devices for the rest of the period, or an SMS pack (PL-13). */
export const INVOICE_KINDS = ["subscription", "upgrade", "credit_note", "addon"];
export const INVOICE_STATUSES = ["open", "awaiting_approval", "paid", "overdue", "credited"];
/** A bKash transaction ID as printed in the app: 8–12 letters and digits; stored upper case. */
export const trxIdSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .pipe(z.string().regex(/^[A-Z0-9]{8,12}$/));
/** A Bangladeshi mobile number as typed; normalised to E.164. */
export const bdPhone = z
    .string()
    .trim()
    .max(32)
    .refine((phone) => normalizePhone(phone) !== null, "Not a Bangladeshi mobile number");
const couponCode = z
    .string()
    .trim()
    .min(1)
    .max(40)
    .transform((value) => value.toUpperCase());
export const planOfferSchema = z.object({
    key: z.string(),
    nameEn: z.string(),
    nameBn: z.string(),
    /** null = not sold on this cycle. */
    prices: z.object({ monthly: paisa().nullable(), yearly: paisa().nullable() }),
    limits: z.array(z.object({ key: z.string(), limit: z.number().int().nullable() })),
    features: z.record(z.string(), z.boolean()),
    // PL-06 (docs/specs/plan-catalogue.md), additive: how the plan is presented, and the feature list by group.
    taglineEn: z.string(),
    taglineBn: z.string(),
    badgeEn: z.string(),
    badgeBn: z.string(),
    isHighlighted: z.boolean(),
    groups: z.array(z.object({
        key: z.string(),
        items: z.array(z.object({
            key: z.string(),
            kind: z.enum(["limit", "credit", "feature", "display"]),
            nameEn: z.string(),
            nameBn: z.string(),
            descriptionEn: z.string(),
            descriptionBn: z.string(),
            unitEn: z.string(),
            unitBn: z.string(),
            /** null = unlimited; on/off for features and display lines. */
            value: z.union([z.number().int(), z.boolean()]).nullable(),
            noteEn: z.string(),
            noteBn: z.string(),
        })),
    })),
});
export const planOfferListSchema = z.object({ data: z.array(planOfferSchema) });
export const paymentRequestSchema = z.object({
    id: z.uuid(),
    invoiceId: z.uuid(),
    method: paymentRequestMethodSchema,
    amountPaisa: paisa(),
    senderPhone: z.string().nullable(),
    trxId: z.string().nullable(),
    note: z.string(),
    status: z.enum(PAYMENT_REQUEST_STATUSES),
    rejectReason: z.string().nullable(),
    createdAt: z.iso.datetime(),
    reviewedAt: z.iso.datetime().nullable(),
});
export const platformPaymentSchema = z.object({
    id: z.uuid(),
    method: paymentRequestMethodSchema,
    amountPaisa: paisa(),
    trxId: z.string().nullable(),
    receivedAt: z.iso.datetime(),
});
export const platformInvoiceSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    kind: z.enum(INVOICE_KINDS),
    planKey: z.string(),
    cycle: billingCycleSchema,
    periodStart: z.iso.datetime(),
    periodEnd: z.iso.datetime(),
    subtotalPaisa: paisa(),
    discountPaisa: paisa(),
    vatBp: z.number().int(),
    vatPaisa: paisa(),
    totalPaisa: paisa(),
    couponCode: z.string().nullable(),
    status: z.enum(INVOICE_STATUSES),
    issuedAt: z.iso.datetime(),
    dueAt: z.iso.datetime(),
    payments: z.array(platformPaymentSchema),
    requests: z.array(paymentRequestSchema),
    /** PL-13: what an `addon` invoice buys (the add-on key and how much of it); null for other kinds. */
    addon: z.object({ key: z.string(), quantity: z.number().int() }).nullable().default(null),
});
export const platformInvoiceListSchema = z.object({
    data: z.array(platformInvoiceSchema),
    nextCursor: z.uuid().nullable(),
});
export const invoiceListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
/** SA-11: where the business stands on the payment timeline (docs/specs/dunning.md). */
export const accountStateSchema = z.object({
    status: z.enum(["trial", "active", "restricted", "suspended", "closed"]),
    /** null: no due date (never dunned). */
    stage: z.enum(["current", "grace", "restricted", "suspended"]).nullable(),
    dueAt: z.iso.datetime().nullable(),
    restrictsAt: z.iso.datetime().nullable(),
    suspendsAt: z.iso.datetime().nullable(),
});
export const billingOverviewSchema = z.object({
    plan: z.object({ key: z.string(), nameEn: z.string(), nameBn: z.string() }),
    tenantStatus: z.string(),
    trialEndsAt: z.iso.datetime().nullable(),
    subscription: z
        .object({
        cycle: billingCycleSchema,
        periodStart: z.iso.datetime(),
        periodEnd: z.iso.datetime(),
        pendingPlanKey: z.string().nullable(),
        pendingCycle: billingCycleSchema.nullable(),
    })
        .nullable(),
    openInvoice: platformInvoiceSchema.nullable(),
    /** Where to send money (billing settings). */
    payTo: z.object({ bkashNumber: z.string(), bkashAccountType: z.enum(["personal", "agent", "merchant"]) }),
    /** A renewal may start from this moment (7 days before the period end). */
    renewalOpensAt: z.iso.datetime().nullable(),
    dunning: accountStateSchema,
});
export const quoteRequestSchema = z.object({
    planKey: z.string().min(1).max(40),
    cycle: billingCycleSchema,
    couponCode: couponCode.optional(),
});
export const quoteSchema = z.object({
    /** `new`: first paid period; `renewal`; `upgrade`: charged now for the rest of the period; `downgrade`: no charge. */
    kind: z.enum(["new", "renewal", "upgrade", "downgrade"]),
    planKey: z.string(),
    cycle: billingCycleSchema,
    subtotalPaisa: paisa(),
    discountPaisa: paisa(),
    vatBp: z.number().int(),
    vatPaisa: paisa(),
    totalPaisa: paisa(),
    periodStart: z.iso.datetime(),
    periodEnd: z.iso.datetime(),
    couponCode: z.string().nullable(),
});
export const createPaymentRequestSchema = quoteRequestSchema
    .extend({
    method: paymentRequestMethodSchema,
    senderPhone: bdPhone.optional(),
    trxId: trxIdSchema.optional(),
    note: z.string().trim().max(500).default(""),
})
    .superRefine((body, ctx) => {
    if (body.method !== "bkash_manual")
        return;
    if (body.senderPhone === undefined)
        ctx.addIssue({ code: "custom", path: ["senderPhone"], message: "Required" });
    if (body.trxId === undefined)
        ctx.addIssue({ code: "custom", path: ["trxId"], message: "Required" });
});
export const downgradeRequestSchema = z.object({
    planKey: z.string().min(1).max(40),
    cycle: billingCycleSchema,
});
// Endpoints ----------------------------------------------------------------------------------------------------
const tag = { tag: "billing" };
const view = { permission: "billing.view" };
const manage = { permission: "billing.manage" };
export const getBilling = defineEndpoint({
    ...tag,
    name: "getBilling",
    method: "GET",
    path: "/v1/billing",
    access: view,
    summary: "The business's plan, subscription, open invoice and where to pay",
    status: 200,
    response: billingOverviewSchema,
});
export const listBillingPlans = defineEndpoint({
    ...tag,
    name: "listBillingPlans",
    method: "GET",
    path: "/v1/billing/plans",
    access: view,
    summary: "Plans on offer with prices and limits",
    status: 200,
    response: planOfferListSchema,
});
/**
 * MK-06: the same public plans for the marketing site's pricing page — no sign-in, no business. Plan names, prices and
 * limits are data (CLAUDE.md §6.6). SaaS only (404 on a dedicated or sandbox instance).
 */
export const listPublicPlans = defineEndpoint({
    ...tag,
    name: "listPublicPlans",
    method: "GET",
    path: "/v1/public/plans",
    access: { public: true },
    summary: "Plans on offer with prices and limits, for the marketing site (cacheable for 5 minutes)",
    status: 200,
    response: planOfferListSchema,
});
export const quoteBilling = defineEndpoint({
    ...tag,
    name: "quoteBilling",
    method: "POST",
    path: "/v1/billing/quote",
    access: manage,
    summary: "What a plan change would cost now (proration, coupon, VAT)",
    body: quoteRequestSchema,
    status: 200,
    response: quoteSchema,
    errors: ["DOWNGRADE_BLOCKED", "COUPON_INVALID"],
});
export const createPaymentRequest = defineEndpoint({
    ...tag,
    name: "createPaymentRequest",
    method: "POST",
    path: "/v1/billing/payment-requests",
    access: manage,
    summary: "Issue the invoice and ask for approval of a bKash or cash payment",
    body: createPaymentRequestSchema,
    idempotent: true,
    status: 201,
    response: platformInvoiceSchema,
    errors: ["TRX_ID_USED", "REQUEST_PENDING", "DOWNGRADE_BLOCKED", "COUPON_INVALID"],
});
export const withdrawPaymentRequest = defineEndpoint({
    ...tag,
    name: "withdrawPaymentRequest",
    method: "POST",
    path: "/v1/billing/payment-requests/{id}/withdraw",
    access: manage,
    summary: "Take back a payment request still waiting for approval",
    params: idParamsSchema,
    status: 200,
    response: paymentRequestSchema,
});
export const scheduleDowngrade = defineEndpoint({
    ...tag,
    name: "scheduleDowngrade",
    method: "POST",
    path: "/v1/billing/downgrade",
    access: manage,
    summary: "Move to a smaller plan at the end of the period",
    body: downgradeRequestSchema,
    status: 200,
    response: billingOverviewSchema,
    errors: ["DOWNGRADE_BLOCKED"],
});
export const cancelDowngrade = defineEndpoint({
    ...tag,
    name: "cancelDowngrade",
    method: "POST",
    path: "/v1/billing/downgrade/cancel",
    access: manage,
    summary: "Keep the current plan after all",
    status: 200,
    response: billingOverviewSchema,
});
export const listPlatformInvoices = defineEndpoint({
    ...tag,
    name: "listPlatformInvoices",
    method: "GET",
    path: "/v1/billing/invoices",
    access: view,
    summary: "Subscription invoices, newest first",
    query: invoiceListQuerySchema,
    status: 200,
    response: platformInvoiceListSchema,
});
export const getPlatformInvoice = defineEndpoint({
    ...tag,
    name: "getPlatformInvoice",
    method: "GET",
    path: "/v1/billing/invoices/{id}",
    access: view,
    summary: "One invoice with its payments and payment requests",
    params: idParamsSchema,
    status: 200,
    response: platformInvoiceSchema,
});
export const platformInvoicePdf = defineEndpoint({
    ...tag,
    name: "platformInvoicePdf",
    method: "POST",
    path: "/v1/billing/invoices/{id}/pdf",
    access: view,
    summary: "The invoice as a PDF (a generated file with a download link)",
    params: idParamsSchema,
    status: 201,
    response: generatedFileResponseSchema,
});
// Events -------------------------------------------------------------------------------------------------------
export const invoiceIssuedEventSchema = z.object({
    invoiceId: z.uuid(),
    number: z.string(),
    kind: z.enum(INVOICE_KINDS),
    totalPaisa: paisa(),
});
export const paymentRequestEventSchema = z.object({
    requestId: z.uuid(),
    invoiceId: z.uuid(),
    method: paymentRequestMethodSchema,
    amountPaisa: paisa(),
    reason: z.string().nullable(),
});
export const paymentApprovedEventSchema = z.object({
    paymentId: z.uuid(),
    requestId: z.uuid(),
    invoiceId: z.uuid(),
    method: paymentRequestMethodSchema,
    amountPaisa: paisa(),
});
export const subscriptionChangedEventSchema = z.object({
    planKey: z.string(),
    cycle: billingCycleSchema,
    periodEnd: z.iso.datetime(),
    reason: z.enum(["paid", "upgrade", "renewal", "downgrade", "downgrade_kept"]),
});
//# sourceMappingURL=billing.js.map